import {
  InclusiveValid,
  SOError,
  type ImageWithFocalPointType,
  type PublicConfigurationT,
  type VideoType,
} from "@swo/shared";
import { SwarovskiOptikPage } from "@swo/sitemap-service";
import { SwarovskiPageAllButData } from "./swarovski-optik-page-renderer";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { OnPageErrorComponent } from "../OnPageErrorsComponent";
import { pipe } from "fp-ts/lib/function";
import { these as D } from "fp-ts";
import React, { useEffect } from "react";
import type { NewsletterTranslation } from "@swo/storefront";
import {
  defaultErrorsHeadline,
  defaultErrorsParagraph,
  ErrorLogsComponent,
} from "../../components/ErrorLogsComponent/ErrorLogsComponent";
import { useToolbarStateCtx } from "../../components/cms/edit-overlay/ToolbarStateProvider";
import type { PageNewsletterData } from "@swo/storefront";

type ExtendedData = {
  newsletter: PageNewsletterData;
};

export function PageRenderer<PageType extends SwarovskiOptikPage, Data extends ExtendedData>(props: {
  data: InclusiveValid<Data>;
  mode: "preview" | "published";
  pageAllButData: SwarovskiPageAllButData<PageType, Data>;
  Component: React.FC<{ data: Data } & SwarovskiPageAllButData<PageType, Data>>;
  config: PublicConfigurationT;
}) {
  const { data, pageAllButData, mode, Component, config } = props;
  const { state } = useToolbarStateCtx();

  const pageId = (pageAllButData.page as any)?.pageId;

  const errorComponent = (errors: NonEmptyArray<SOError>) => (
    <OnPageErrorComponent
      errors={errors}
      pageId={pageId}
      mode={mode}
      generalTranslations={pageAllButData.generalTranslations}
      config={config}
    />
  );

  return pipe(
    data,
    D.fold(
      errors => {
        if (state) {
          switch (state.errorsStrategy) {
            case "fine-grained": {
              return (
                <ErrorLogsComponent
                  errors={errors}
                  pageId={pageId}
                  config={config}
                  headline={"Page error"}
                  paragraph="There was a page-level error. Please take a look at the following errors."
                />
              );
            }
            case "fail-fast": {
              return errorComponent(errors);
            }
            default: {
              const _: never = state.errorsStrategy;

              return _;
            }
          }
        } else {
          return errorComponent(errors);
        }
      },
      validData => <Component {...pageAllButData} data={validData} />,
      (errors, validData) => {
        const component = <Component {...pageAllButData} data={validData} />;

        if (state) {
          switch (state.errorsStrategy) {
            case "fine-grained": {
              return component;
            }
            case "fail-fast": {
              return (
                <ErrorLogsComponent
                  errors={errors}
                  pageId={pageId}
                  config={config}
                  headline={defaultErrorsHeadline}
                  paragraph={defaultErrorsParagraph}
                />
              );
            }
            default: {
              const _: never = state.errorsStrategy;

              return _;
            }
          }
        } else {
          return props.mode === "preview" ? errorComponent(errors) : component;
        }
      },
    ),
  );
}
